import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import cx from 'classnames';

import { Icon } from 'components/Icon';
import { Pill } from 'components/Misc/Pill';
import { Link, LinkProps } from 'components/Typography';
import { VisuallyHidden } from 'components/Utilities';
import { LanguageContext } from 'containers/LanguageProvider';
import { ICON } from 'utilsTS/icon';
import { omitProps, isElementOfComponent } from 'utilsTS/react';

export interface SecondaryNavigationItemProps extends LinkProps {
    href?: string;
    label?: string;
    icon?: ICON;
    iconActive?: ICON;
    badge?: React.ReactNode;
    pill?: React.ReactNode | number;
    selected?: boolean;
    linkClassName?: string;
    external?: boolean;
    crossLink?: boolean;
    items?: SecondaryNavigationItemProps[];
    showIcon?: boolean;
    isExpanded?: boolean;
    isSection?: boolean;
    onClick?: React.MouseEventHandler<HTMLElement>;
}

// eslint-disable-next-line complexity
export const SecondaryNavigationItem: React.FC<SecondaryNavigationItemProps> = ({
    href,
    label,
    icon: defaultIcon,
    iconActive,
    badge,
    pill,
    selected,
    showIcon,
    className,
    linkClassName,
    external,
    crossLink,
    isExpanded = true,
    onClick = noop,
    ...rest
}) => {
    const icon = selected ? iconActive || defaultIcon : defaultIcon;
    const classNames = cx(selected && 'g-sidenav-selected', className);
    const labelClassName = showIcon && !icon ? 'g-sidenav-offseted-label' : '';
    const { getString } = React.useContext(LanguageContext);

    return (
        <li className={classNames}>
            <Link
                {...omitProps(rest, SecondaryNavigationItem)}
                href={href}
                className={linkClassName}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                onClick={onClick}
            >
                {icon && showIcon && (
                    <Icon
                        icon={icon}
                        size={Icon.SIZE.SIZE_24}
                        color={Icon.COLOR.UI_BLUE}
                        className={cx('g-sidenav-icon', !selected && 'g-faded')}
                    />
                )}

                {isExpanded && (
                    <>
                        <span className={labelClassName}>{label}</span>

                        {external && (
                            <Icon
                                ariaLabel={` ${getString('common.externalLink')}`}
                                icon={Icon.ICON.ARROW_OUT}
                                size={Icon.SIZE.SIZE_24}
                                className="g-sidenav-external-icon"
                            />
                        )}

                        {crossLink && (
                            <Icon
                                icon={Icon.ICON.CROSS_LINK}
                                size={Icon.SIZE.SIZE_24}
                                className="g-sidenav-crosslink-icon"
                            />
                        )}

                        {badge}
                    </>
                )}

                {!!pill && isElementOfComponent(pill, Pill) && pill}
                {typeof pill === 'number' && pill !== 0 && (
                    <Pill value={pill} variant={Pill.VARIANT.UNREAD} />
                )}

                {!isExpanded && <VisuallyHidden>{label}</VisuallyHidden>}
            </Link>
        </li>
    );
};

SecondaryNavigationItem.displayName = 'SecondaryNavigationItem';
SecondaryNavigationItem.propTypes = {
    id: PropTypes.string,
    href: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.oneOf(Object.values(Icon.ICON)),
    iconActive: PropTypes.oneOf(Object.values(Icon.ICON)),
    pill: PropTypes.oneOfType([PropTypes.node, PropTypes.number]),
    badge: PropTypes.node,
    selected: PropTypes.bool,
    className: PropTypes.string,
    linkClassName: PropTypes.string,
    external: PropTypes.bool,
    crossLink: PropTypes.bool,
    showIcon: PropTypes.bool,
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,
};
